import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Store } from 'components/common/types/Store.types';
import { useDispatch, useSelector } from 'react-redux';
import { isOneFilterSelected, isOneTargetSelected } from 'utils/objectives';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { updateIsDiscardModalOpen, updateObjectiveToCancel } from 'store/objective/reducer';
import { CampaignType } from '../PlannerSections/types';

type UseUnsavedChange = (callback: VoidFunction) => void;

const useUnsavedChange = (): UseUnsavedChange => {
  const dispatch = useDispatch();

  const location = useLocation();
  const isProduct = location.pathname.includes('product');

  const currentPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);
  const campaignType = useSelector((state: Store) => state.dealManagement.campaignType);
  const currentLineId = useSelector((state: Store) => state.dealManagement.commonDeal?.currentLine?.lineId);
  const productObjectives = useSelector((state: Store) => state.productManagement.newProduct?.objectives?.value || []);
  const dealLineObjectives = useSelector((state: Store) => state.dealManagement.directSales?.objectives);
  const objectives = isProduct ? productObjectives : dealLineObjectives;

  const {
    data: { objectiveGroupId: activeObjectiveGroupId },
    settings: { isChange: isUnsavedObjectiveChange, isSubsectionChange: isUnsavedObjectiveSubsectionChange },
  } = useSelector((state: Store) => state.objective);

  return useCallback(
    (callback: VoidFunction) => {
      if (campaignType === CampaignType.DIRECT && currentPanelType.includes(PlannerSecondaryPanelType.OBJECTIVE)) {
        const initialObjectiveState = objectives.find(
          (objective) => objective.objectiveGroupId === activeObjectiveGroupId,
        );

        const isUnsavedObjective =
          initialObjectiveState &&
          !(isOneTargetSelected(initialObjectiveState) && isOneFilterSelected(initialObjectiveState));

        const isExistingObjectiveWithUnsavedChanges = isUnsavedObjectiveChange || isUnsavedObjectiveSubsectionChange;

        if (isUnsavedObjective) {
          dispatch(
            updateObjectiveToCancel({
              code: initialObjectiveState.objectiveGroupId.toString(),
              name: initialObjectiveState.name,
              lineId: currentLineId,
            }),
          );
          return;
        }

        if (isExistingObjectiveWithUnsavedChanges) {
          dispatch(updateIsDiscardModalOpen(true));
          return;
        }
      }

      callback();
    },
    [
      objectives,
      campaignType,
      currentPanelType,
      currentLineId,
      isUnsavedObjectiveChange,
      isUnsavedObjectiveSubsectionChange,
    ],
  );
};

export default useUnsavedChange;
